// Dirty-detection and save-payload logic extracted verbatim from
// components/defaultLayout/EntityDialog&Forms/EntityMaskForm.vue (the
// inputFieldsOnlyIEntityMaskData / isDirty computeds and the isDirtyIField /
// getDirtyFieldsObject / compensateForClearableClearSettingWrongFalseyValue
// methods). Pulled into a pure, framework-agnostic module so the save path is
// locked by unit tests that survive the Nuxt 2 -> Nuxt 3 migration; the
// component now forwards to these. Component state that used to be read off
// `this` (isEditing, entityData) is passed in explicitly. Inline console.debug
// noise from the originals is intentionally dropped.

import { isEqual } from 'lodash'
import type {
  IEntityMaskData,
  IHasIndexSignature,
  TEntityMaskItem,
  TEntityMaskDataValueTypes,
} from '~/types/models'
import { isEntityMaskData } from '~/types/models'
import {
  splitWrapperPath,
  setValueAtWrapperPath,
} from '~/cynqueModules/wrapperObjectPath'
import { convertNumericTextFieldTypeEntityMaskDataValueToNumber } from '~/cynqueModules/convertNumericTextFieldTypeEntityMaskDataValueToNumber'
import { joinDelimitedList } from '~/cynqueModules/componentHelpers/entityMaskForm/delimitedList'

const mn = '[cynqueModules/componentHelpers/entityMaskForm/dirtyFields] |'

// The `inputFieldsOnlyIEntityMaskData` computed: keep only real data fields,
// dropping the layout-only items (dividers, subtitles, spacers).
export function filterToEntityMaskData(
  inputFields: TEntityMaskItem[]
): IEntityMaskData[] {
  return inputFields.filter(isEntityMaskData)
}

// Per-field dirty predicate. Extracted from `isDirtyIField`, whose own comment
// asked for `isEditing` to be passed in rather than read off `this`.
export function isDirtyIField(
  ifield: TEntityMaskItem,
  isEditing: boolean
): boolean {
  return (
    isEntityMaskData(ifield) &&
    !ifield.neverTreatAsDirty &&
    !(ifield.unrendered && ifield.ignoreIfUnrendered) &&
    ('doAlwaysTreatAsDirty' in ifield ||
      ('doAlwaysTreatAsDirtyWhenEditing' in ifield && isEditing) ||
      (!isEqual(ifield.start, ifield.value) && !('isForDisplayOnly' in ifield)))
  )
}

// The `isDirty` computed: the whole form is dirty when at least one field is
// dirty, except the case where the only dirty field is `version` (bumped
// silently and never on its own a reason to enable Save).
export function isFormDirty(
  inputFields: TEntityMaskItem[],
  isEditing: boolean
): boolean {
  const dirtyFields = inputFields.filter((ifield) =>
    isDirtyIField(ifield, isEditing)
  )
  const onlyDirtyFieldIsVersion =
    dirtyFields.length === 1 &&
    isEntityMaskData(dirtyFields[0]) &&
    dirtyFields[0].name === 'version'
  return dirtyFields.length > 0 && !onlyDirtyFieldIsVersion
}

// The fields a save sends: every dirty field, plus the fields any dirty field
// names in `sendWith` (dirty or not), in form order.
export function fieldsToSave(
  inputFields: TEntityMaskItem[],
  isEditing: boolean
): IEntityMaskData[] {
  const fields = filterToEntityMaskData(inputFields)
  const dirty = fields.filter((ifield) => isDirtyIField(ifield, isEditing))
  const companions = new Set(dirty.flatMap((ifield) => ifield.sendWith ?? []))
  return fields.filter(
    (ifield) => dirty.includes(ifield) || companions.has(ifield.name)
  )
}

// Build the changed-fields save payload by writing each dirty field's value at
// its path (and each `sendWith` companion's -- see fieldsToSave). A field's path is its wrapperObjectName (a dot-path of arbitrary
// depth, e.g. 'supplierProduct.product') plus its name, or just its name when
// unwrapped. setValueAtWrapperPath seeds each intermediate object from
// entityData, so the full wrapper object (with its untouched siblings) is
// carried through at every level, and fields that share a parent path coexist
// without clobbering one another.
export function getDirtyFieldsObject(opts: {
  inputFields: TEntityMaskItem[]
  entityData: object | undefined
  isEditing: boolean
}): IHasIndexSignature {
  const fn = `${mn} getDirtyFieldsObject |`
  const { inputFields, entityData, isEditing } = opts
  const dirtyFieldsObject = {} as IHasIndexSignature
  fieldsToSave(inputFields, isEditing).forEach((ifield) => {
    // NOTE: Don't love doing the string -> number conversions for
    // numeric textField types, such as 'currencyTextField', invisibly
    // here, but can't happen earlier or it will mess up the 'start' vs
    // 'value' check for changed data, and this is the last point
    // at which the inputField type, such as 'currencyTextField', is
    // still part of the data object.
    const convertedValue =
      convertNumericTextFieldTypeEntityMaskDataValueToNumber({
        callingFn: fn,
        ifield,
      })
    const unjoinedValue =
      convertedValue !== undefined ? convertedValue : ifield.value
    // A list edited over a stored string goes back to the string here.
    const value =
      ifield.delimitedStringSeparator !== undefined
        ? (joinDelimitedList(
            unjoinedValue,
            ifield.delimitedStringSeparator,
            ifield.itemValue
          ) as TEntityMaskDataValueTypes)
        : unjoinedValue
    const segments = ifield.wrapperObjectName
      ? [...splitWrapperPath(ifield.wrapperObjectName), ifield.name]
      : [ifield.name]
    setValueAtWrapperPath(
      dirtyFieldsObject,
      segments,
      value,
      entityData as IHasIndexSignature | undefined
    )
  })
  return dirtyFieldsObject
}

// If the user clicks the 'X' clearable button in a select field returning an
// object, Vuetify sets value to null regardless of the actual sentinel value
// (which, for an object, might be either null or undefined depending on how it
// was set up and on any specified default value). This restores the intended
// falsey value so ifield.start === ifield.value and isDirty evaluates to false
// for a field that started out undefined and whose default was not null.
export function compensateForClearableClearSettingWrongFalseyValue(
  ifield: IEntityMaskData
): TEntityMaskDataValueTypes {
  return ifield.value === null &&
    ifield.default !== null &&
    ifield.start === undefined
    ? undefined
    : ifield.value
}
